import axios from '../utils/axios';
import {
  ClaimFromDispensePayload,
  DispenseVoucherQuote,
  GosFormData,
  ListClaimsQuery,
  NhsVoucherRate,
  PcseClaimSummaryPage,
  PcseClaim,
  PcseClaimStatus,
  PcsePayloadLog,
  PcsePingResult,
  SubmitClaimPayload,
  VoucherQuote,
  VoucherQuoteRequest,
} from '../@types/pcse';

/**
 * eGOS (PCSE) claims API client. Thin wrapper over the shared axios instance —
 * the axios response interceptor already unwraps error bodies, so callers only
 * need `res.data` on success.
 *
 * Backend routes are mounted at `/pcse` (no global prefix in the NestJS app).
 */
export class PcseService {
  /** Connectivity check against the configured PCSE environment. */
  ping() {
    return axios.get<PcsePingResult>('/pcse/ping');
  }

  /** Create and (optionally, when `draft: false`) submit a claim. */
  submitClaim(payload: SubmitClaimPayload) {
    return axios.post<PcseClaim>('/pcse/claims/submit', payload);
  }

  /** Resubmit a stored draft/error/rejected claim. */
  resubmitClaim(id: string) {
    return axios.post<PcseClaim>(`/pcse/claims/${id}/resubmit`);
  }

  /** Poll PCSE for the latest adjudication status. */
  getStatus(id: string) {
    return axios.get<PcseClaim>(`/pcse/claims/${id}/status`);
  }

  getClaim(id: string) {
    return axios.get<PcseClaim>(`/pcse/claims/${id}`);
  }

  /**
   * The claims list: filtered, paginated summaries plus money totals by status.
   *
   * Returns lightweight rows, not full claims — the server strips the signature
   * images and submitted payload so a page of claims is kilobytes, not
   * megabytes of base64.
   */
  listClaims(params?: ListClaimsQuery) {
    return axios.get<PcseClaimSummaryPage>('/pcse/claims', { params });
  }

  /**
   * Run the adjudication poll now instead of waiting for the hourly job.
   * Manager/admin only on the backend.
   */
  pollStatuses() {
    return axios.post<{ polled: number; changed: number }>(
      '/pcse/claims/poll-statuses'
    );
  }

  /** Full XML audit trail (manager only on the backend). */
  getAuditLog(id: string) {
    return axios.get<PcsePayloadLog[]>(`/pcse/claims/${id}/audit`);
  }

  /** GOS3 voucher lookup by code. */
  retrieveVoucher(voucherCode: string) {
    return axios.get('/pcse/vouchers', { params: { voucherCode } });
  }

  // ── NHS voucher rates ─────────────────────────────────────────────────────

  /**
   * The NHS voucher and supplement values in force. These are set by NHS
   * England and change after negotiation, so they live in the database and are
   * always fetched — never hard-coded in the UI.
   */
  listVoucherRates(onDate?: string) {
    return axios.get<NhsVoucherRate[]>('/pcse/voucher-rates', {
      params: onDate ? { onDate } : undefined,
    });
  }

  /** Full rate history including superseded periods (manager only). */
  getVoucherRateHistory(code?: string) {
    return axios.get<NhsVoucherRate[]>('/pcse/voucher-rates/history', {
      params: code ? { code } : undefined,
    });
  }

  /**
   * Derive the band from a prescription and value it.
   *
   * The server is authoritative for every figure shown to a patient: the same
   * calculation later produces the GOS3 claim, so a client-side estimate could
   * only ever disagree with what is actually claimed.
   */
  quoteVoucher(payload: VoucherQuoteRequest) {
    return axios.post<VoucherQuote>('/pcse/voucher-rates/quote', payload);
  }

  /** Record a new value for a rate code from a date (a negotiated uplift). */
  setVoucherRate(payload: Partial<NhsVoucherRate> & { code: string }) {
    return axios.post<NhsVoucherRate>('/pcse/voucher-rates', payload);
  }

  /** Correct a rate row in place — for typos, not for an uplift. */
  updateVoucherRate(id: string, payload: Partial<NhsVoucherRate>) {
    return axios.patch<NhsVoucherRate>(`/pcse/voucher-rates/${id}`, payload);
  }

  deleteVoucherRate(id: string) {
    return axios.delete(`/pcse/voucher-rates/${id}`);
  }

  /** Restore the published NHS values, discarding local edits (admin only). */
  resetVoucherRates() {
    return axios.post<NhsVoucherRate[]>('/pcse/voucher-rates/reset');
  }

  // ── Dispense → claim ──────────────────────────────────────────────────────

  /** What the NHS covers on a dispense, and what the patient owes. */
  getDispenseVoucher(dispenseId: number) {
    return axios.get<DispenseVoucherQuote>(
      `/pcse/dispenses/${dispenseId}/voucher`
    );
  }

  /** Raise the GOS3 claim that recovers a dispense's voucher from PCSE. */
  claimForDispense(dispenseId: number, payload: ClaimFromDispensePayload = {}) {
    return axios.post<PcseClaim>(
      `/pcse/dispenses/${dispenseId}/claim`,
      payload
    );
  }

  listClaimsForDispense(dispenseId: number) {
    return axios.get<PcseClaim[]>(`/pcse/dispenses/${dispenseId}/claims`);
  }

  // ── Printable GOS paper form ──────────────────────────────────────────────

  /**
   * Data for the official NHS GOS paper form for a claim.
   *
   * Built server-side from the claim's own stored payload, so the printed form
   * shows what was actually claimed rather than a re-derivation against today's
   * rate table.
   */
  getClaimForm(claimId: string) {
    return axios.get<GosFormData>(`/pcse/claims/${claimId}/form`);
  }
}

export const pcseService = new PcseService();
